import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Row,
  Col,
  Input,
  Select,
  Button,
  notification,
  Table,
  Tag,
  Alert,
  Space,
  Typography,
  Checkbox,
  Radio,
  Progress,
} from 'antd';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import lib, { myRequestObj, writeArrayToCSV } from '../../assets/utils/lib';
import { states } from '../../assets/utils/config';
import { usePermissions } from '../../assets/utils/usePermissions';
import { hasAnyPermissionWithLegacy } from '../../assets/utils/permissions';
import CustomFileUploadModal from '../compoinents/UploadModal';
import MainFacilitySelect from './MainFacilitySelect';
import { ADMIN_REVIEW_MODAL_CLASS } from '../dashboard/utils';
import {
  PROVIDERS_BATCH_CSV_HEADERS,
  getFacilityOnboardingStatusMeta,
  normalizeFacilityBulkResults,
} from './facilityUtils';

const { TextArea } = Input;
const { Text } = Typography;

function onboardingFromCreateResponse(data) {
  const nested = data?.onboarding || {};
  return {
    onboarding_status:
      nested.onboarding_status
      || data?.onboarding_status
      || null,
    message: nested.message || data?.message || '',
  };
}

export default function FacilityOnboardingModal({ open, onClose, onSuccess, user }) {
  const { permissions } = usePermissions();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showBatch, setShowBatch] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [bulkJobs, setBulkJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [facilityTypes, setFacilityTypes] = useState([]);
  const [tierOptions, setTierOptions] = useState([
    { label: 'All Plans (no minimum)', value: null, tier_name: 'all plans' },
  ]);
  const [loadingTiers, setLoadingTiers] = useState(false);

  // `user` is a prop here (this modal is rendered from more than one screen), so the
  // pure helper is used with the session permission map rather than the bound hook form.
  const canOnboard = useMemo(
    () => hasAnyPermissionWithLegacy(['facilities.manage', 'admin.*'], permissions, user, [1, 2]),
    [permissions, user],
  );

  const canSubmit = facilityTypes.length > 0 && !loadingTiers;

  const loadBulkJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.listFacilityBulkUploadJobsV2({ limit: 10 }));
      if (!parsed.ok) {
        setBulkJobs([]);
        return;
      }
      const jobs = parsed.data?.jobs || parsed.data?.rows || [];
      setBulkJobs(Array.isArray(jobs) ? jobs : []);
    } catch {
      setBulkJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setFormData({});
    setBulkResults(null);
    setBulkJobId(null);
    loadBulkJobs();

    myRequestObj('facilityTypes').get()
      .then((response) => response?.data)
      .then((data) => {
        if (Number(data?.error) === 0) {
          setFacilityTypes((data?.data || []).map((type) => ({
            value: type?.id,
            label: type?.name,
          })));
        }
      })
      .catch(() => {});

    let mounted = true;
    const loadTiers = async () => {
      setLoadingTiers(true);
      try {
        const resp = await lib.getAdminTiers({ scope: 'active' });
        const body = resp?.data;
        if (body?.error || !Array.isArray(body?.data?.tiers)) return;
        const options = body.data.tiers
          .filter((tier) => Number.isFinite(Number(tier?.tier_id)))
          .map((tier) => ({
            label: `${tier?.tier_name || `Tier ${tier?.tier_id}`} (Rank: ${tier?.tier_rank ?? '--'})`,
            value: Number(tier.tier_id),
            tier_name: tier?.tier_name || '',
          }));
        if (mounted) {
          setTierOptions([
            { label: 'All Plans (no minimum)', value: null, tier_name: 'all plans' },
            ...options,
          ]);
        }
      } finally {
        if (mounted) setLoadingTiers(false);
      }
    };
    loadTiers();
    return () => {
      mounted = false;
    };
  }, [open, loadBulkJobs]);

  const resetAndClose = () => {
    setFormData({});
    setBulkResults(null);
    setBulkJobId(null);
    onClose?.();
  };

  const downloadReport = async (jobId) => {
    if (!jobId) {
      notification.warning({ message: 'No report available for this upload yet', placement: 'topLeft' });
      return;
    }
    setDownloadingJobId(jobId);
    try {
      await lib.downloadFacilityBulkUploadReportV2(jobId);
      notification.success({ message: 'Report downloaded', placement: 'topLeft' });
    } catch (e) {
      notification.error({
        message: e?.message || 'Unable to download report',
        placement: 'topLeft',
      });
    } finally {
      setDownloadingJobId(null);
    }
  };

  const saveProvider = async () => {
    if (
      !formData?.facility_name
      || !formData?.facility_type_id
      || formData?.facility_tier_id === undefined
      || !formData?.facility_email
      || !formData?.facility_phone
      || !formData?.facility_state
      || !formData?.facility_city
      || !formData?.facility_address
    ) {
      notification.error({ message: 'Please fill the compulsory fields', placement: 'topLeft' });
      return;
    }

    setLoading(true);
    try {
      const selectedTier = tierOptions.find((tier) => tier?.value === formData.facility_tier_id);
      const payload = {
        name: formData.facility_name.trim(),
        address: formData.facility_address.trim(),
        city: formData.facility_city.trim(),
        state: formData.facility_state,
        contact_email: formData.facility_email.trim(),
        contact_phone: formData.facility_phone.trim(),
        facility_tier_id: formData.facility_tier_id ?? null,
        facility_type_id: formData.facility_type_id,
        facility_type: formData.facility_type,
        plan_type: selectedTier?.tier_name || 'all plans',
      };
      if (formData.facility_designation) {
        payload.contact_person = formData.facility_designation.trim();
      }
      // Onboard directly as a branch of an existing main facility. The server
      // rejects a parent that is itself a branch, so no facility row is created
      // on a bad selection.
      if (formData.parent_facility_id) {
        payload.parent_facility_id = formData.parent_facility_id;
        if (formData.inherit_tariff) {
          payload.inherit_tariff = true;
          payload.tariff_mode = formData.tariff_mode || 'linked';
        }
      }

      const parsed = lib.parseApiEnvelope(await lib.createFacilityV2(payload));
      if (!parsed.ok) {
        notification.error({
          message: parsed.message || 'Failed to create facility',
          placement: 'topLeft',
        });
        return;
      }

      const onboard = onboardingFromCreateResponse(parsed.data);
      const statusMeta = getFacilityOnboardingStatusMeta(onboard.onboarding_status);
      notification.success({
        message: parsed.message || 'Facility created',
        description: onboard.onboarding_status
          ? statusMeta.label
          : (onboard.message || undefined),
        duration: 6,
        placement: 'topLeft',
      });
      resetAndClose();
      onSuccess?.(parsed.data);
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Failed to create facility',
        placement: 'topLeft',
      });
    } finally {
      setLoading(false);
    }
  };

  const uploadBatch = async (files) => {
    const file = files?.[0];
    if (!file) {
      notification.warning({ message: 'No file selected' });
      return;
    }

    const jobId = (window.crypto?.randomUUID?.())
      || `FAC_BULK_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    setUploading(true);
    setUploadProgress({ processed: 0, total: 0, percent: 0 });
    notification.info({
      message: 'Bulk upload in progress — do not close this window.',
      key: 'facility-bulk',
      icon: <LoadingOutlined />,
      duration: 0,
      placement: 'topLeft',
    });

    const pollTimer = setInterval(async () => {
      try {
        const parsed = lib.parseApiEnvelope(await lib.getFacilityBulkUploadJobStatusV2(jobId));
        if (parsed.ok && parsed.data) {
          setUploadProgress({
            processed: parsed.data.processed || 0,
            total: parsed.data.total_rows || 0,
            percent: parsed.data.percent || 0,
          });
        }
      } catch (e) {
        // Job row may not exist yet in the first tick, or the network hiccuped — next
        // tick will catch up, no need to surface this.
      }
    }, 1500);

    try {
      const parsed = lib.parseApiEnvelope(await lib.bulkCreateFacilitiesFileV2(file, jobId));
      if (!parsed.ok) {
        notification.error({
          message: parsed.message || 'Bulk upload failed',
          key: 'facility-bulk',
          placement: 'topLeft',
        });
        return;
      }

      const results = normalizeFacilityBulkResults(parsed.data);
      setBulkResults(results.length ? results : null);
      setBulkJobId(parsed.data?.job_id || null);
      setShowBatch(false);
      loadBulkJobs();
      const summary = parsed.data?.summary;
      notification.success({
        message: parsed.message || 'Bulk upload completed',
        description: summary
          ? `Created ${summary.created || 0}, skipped ${summary.skipped || 0}, failed ${summary.failed || 0}`
          : undefined,
        key: 'facility-bulk',
        placement: 'topLeft',
      });
      onSuccess?.(parsed.data);
    } catch (error) {
      notification.error({
        message: error?.message || 'Bulk upload failed',
        key: 'facility-bulk',
        placement: 'topLeft',
      });
    } finally {
      clearInterval(pollTimer);
      setUploading(false);
      setUploadProgress(null);
      setTimeout(() => notification.destroy('facility-bulk'), 2000);
    }
  };

  const bulkColumns = [
    {
      title: 'Name',
      key: 'name',
      ellipsis: true,
      render: (_, row) => row.name || row.facility_name || '—',
    },
    {
      title: 'Created',
      key: 'created',
      width: 100,
      render: (_, row) => {
        if (row.facility_id != null || row.activated === true || row._result === 'created') {
          return <Tag color="success">Yes</Tag>;
        }
        if (row._result === 'skipped' || row.skipped) {
          return <Tag>Skipped</Tag>;
        }
        if (row._result === 'failed' || row.error) {
          return <Tag color="error">No</Tag>;
        }
        return <Tag>No</Tag>;
      },
    },
    {
      title: 'Onboarding',
      key: 'onboarding',
      width: 220,
      render: (_, row) => {
        const status =
          row.onboarding_status
          || row.onboarding?.onboarding_status
          || null;
        const meta = getFacilityOnboardingStatusMeta(status);
        return status ? <Tag color={meta.color}>{meta.label}</Tag> : '—';
      },
    },
    {
      title: 'Reasons',
      key: 'reasons',
      ellipsis: true,
      render: (_, row) => {
        const reason =
          row.reason
          || row.reasons
          || (Array.isArray(row.skip_reasons) ? row.skip_reasons.join('; ') : null)
          || (Array.isArray(row.errors) ? row.errors.join('; ') : null)
          || row.message
          || row.onboarding?.message
          || '';
        return reason || '—';
      },
    },
  ];

  const jobColumns = [
    {
      title: 'When',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v) => v || '—',
    },
    {
      title: 'File',
      dataIndex: 'source_filename',
      key: 'source_filename',
      ellipsis: true,
      render: (v, row) => v || row.source_format || 'json',
    },
    {
      title: 'Summary',
      key: 'summary',
      render: (_, row) =>
        `${row.created_count ?? 0} created · ${row.skipped_count ?? 0} skipped · ${row.failed_count ?? 0} failed`,
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, row) => (
        <Button
          size="small"
          type="link"
          icon={<DownloadOutlined />}
          loading={downloadingJobId === row.job_id}
          onClick={() => downloadReport(row.job_id)}
        >
          Report
        </Button>
      ),
    },
  ];

  if (!canOnboard) return null;

  return (
    <>
      <Modal
        title="Onboard new facility"
        centered
        open={open}
        onCancel={resetAndClose}
        width={800}
        destroyOnClose
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        footer={(
          <>
            <Button disabled={loading || uploading} onClick={() => setShowBatch(true)}>
              Batch upload
            </Button>
            <Button
              type="primary"
              disabled={!canSubmit || loading}
              onClick={saveProvider}
              loading={loading}
            >
              Add facility
            </Button>
            <Button type="default" disabled={loading || uploading} onClick={resetAndClose}>
              Close
            </Button>
          </>
        )}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Portal login uses the facility email. A temporary password is emailed automatically (not shown here). Gym/Spa types skip portal invites."
        />
        <Row gutter={[16, 12]}>
          <Col span={24}>
            <label className="facility-form-label">Facility name *</label>
            <Input
              value={formData?.facility_name}
              onChange={(e) => setFormData((d) => ({ ...d, facility_name: e.target.value }))}
              placeholder="Full name (include branch area if applicable)"
            />
          </Col>
          <Col span={12}>
            <label className="facility-form-label">Facility type *</label>
            <Select
              value={formData?.facility_type_id}
              onChange={(value) => setFormData((d) => ({
                ...d,
                facility_type_id: value,
                facility_type: facilityTypes.find((ft) => ft.value === value)?.label,
              }))}
              options={facilityTypes}
              placeholder="Hospital, dental, lab…"
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={12}>
            <label className="facility-form-label">Minimum tier *</label>
            <Select
              value={formData?.facility_tier_id}
              onChange={(value) => setFormData((d) => ({ ...d, facility_tier_id: value }))}
              options={tierOptions}
              loading={loadingTiers}
              placeholder="Facility tier"
              style={{ width: '100%' }}
              allowClear
              onClear={() => setFormData((d) => ({ ...d, facility_tier_id: undefined }))}
            />
          </Col>
          <Col span={12}>
            <label className="facility-form-label">State *</label>
            <Select
              value={formData?.facility_state}
              onChange={(value) => setFormData((d) => ({ ...d, facility_state: value }))}
              options={states}
              placeholder="State"
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={12}>
            <label className="facility-form-label">City *</label>
            <Input
              value={formData?.facility_city}
              onChange={(e) => setFormData((d) => ({ ...d, facility_city: e.target.value }))}
              placeholder="City or LGA"
            />
          </Col>
          <Col span={24}>
            <label className="facility-form-label">Address *</label>
            <TextArea
              rows={2}
              value={formData?.facility_address}
              onChange={(e) => setFormData((d) => ({ ...d, facility_address: e.target.value }))}
              placeholder="Physical address"
            />
          </Col>
          <Col span={12}>
            <label className="facility-form-label">Email * (portal username)</label>
            <Input
              value={formData?.facility_email}
              onChange={(e) => setFormData((d) => ({ ...d, facility_email: e.target.value }))}
            />
          </Col>
          <Col span={12}>
            <label className="facility-form-label">Phone *</label>
            <Input
              value={formData?.facility_phone}
              onChange={(e) => setFormData((d) => ({ ...d, facility_phone: e.target.value }))}
            />
          </Col>
          <Col span={24}>
            <label className="facility-form-label">Contact person</label>
            <Input
              value={formData?.facility_designation}
              onChange={(e) => setFormData((d) => ({ ...d, facility_designation: e.target.value }))}
            />
          </Col>
          <Col span={24}>
            <label className="facility-form-label">Branch of (optional)</label>
            <MainFacilitySelect
              value={formData?.parent_facility_id}
              onChange={(value) => setFormData((d) => ({
                ...d,
                parent_facility_id: value ?? null,
                // Dropping the parent drops the inheritance choice with it.
                inherit_tariff: value ? d.inherit_tariff : false,
              }))}
              placeholder="Standalone facility — leave empty unless this is a branch"
              size="middle"
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Branch facilities share encounter codes with their main facility, and
              their encounters roll up to the main facility&apos;s admin.
            </Text>
          </Col>
          {formData?.parent_facility_id ? (
            <Col span={24}>
              <Checkbox
                checked={!!formData?.inherit_tariff}
                onChange={(e) => setFormData((d) => ({ ...d, inherit_tariff: e.target.checked }))}
              >
                Use the main facility&apos;s tariff
              </Checkbox>
              {formData?.inherit_tariff ? (
                <div style={{ marginTop: 8, marginLeft: 24 }}>
                  <Radio.Group
                    value={formData?.tariff_mode || 'linked'}
                    onChange={(e) => setFormData((d) => ({ ...d, tariff_mode: e.target.value }))}
                  >
                    <Space direction="vertical" size={2}>
                      <Radio value="linked">
                        Linked — the branch tracks the main facility&apos;s tariff as it changes
                      </Radio>
                      <Radio value="copy">
                        Copy — take a snapshot the branch can edit on its own
                      </Radio>
                    </Space>
                  </Radio.Group>
                </div>
              ) : null}
            </Col>
          ) : null}
        </Row>

        {Array.isArray(bulkResults) && bulkResults.length > 0 ? (
          <div style={{ marginTop: 20 }}>
            <Space style={{ marginBottom: 8 }} align="center" wrap>
              <Text strong>Bulk results</Text>
              {bulkJobId ? (
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  loading={downloadingJobId === bulkJobId}
                  onClick={() => downloadReport(bulkJobId)}
                >
                  Download report
                </Button>
              ) : null}
              <Button size="small" type="link" onClick={() => { setBulkResults(null); setBulkJobId(null); }}>
                Clear
              </Button>
            </Space>
            <Table
              size="small"
              rowKey={(row, idx) =>
                String(row.facility_id ?? row.row ?? row.name ?? idx)
              }
              columns={bulkColumns}
              dataSource={bulkResults}
              pagination={{ pageSize: 8, hideOnSinglePage: true }}
              scroll={{ x: true }}
            />
          </div>
        ) : null}

        <div style={{ marginTop: 20 }}>
          <Space style={{ marginBottom: 8 }} align="center">
            <Text strong>Recent bulk uploads</Text>
            <Button size="small" type="link" loading={loadingJobs} onClick={loadBulkJobs}>
              Refresh
            </Button>
          </Space>
          <Table
            size="small"
            loading={loadingJobs}
            rowKey={(row) => String(row.job_id || row.id)}
            columns={jobColumns}
            dataSource={bulkJobs}
            pagination={false}
            locale={{ emptyText: 'No saved upload reports yet (run migration if empty after uploads)' }}
            scroll={{ x: true }}
          />
        </div>
      </Modal>

      <CustomFileUploadModal
        visible={showBatch}
        onOk={uploadBatch}
        onCancel={() => { setShowBatch(false); setUploading(false); }}
        loading={uploading}
        name="Bulk facility onboarding"
        showDownload
        id="upload_batch_facilities"
        accept=".csv,.tsv,.xlsx,.xls,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        chooseLabel="Choose CSV, TSV, or Excel file"
        instruction={(
          <>
            {uploading && uploadProgress ? (
              <div style={{ width: '100%', maxWidth: 420, margin: '0 auto 8px' }}>
                <Progress percent={uploadProgress.percent} status="active" />
                <p style={{ textAlign: 'center', color: '#64748b', fontSize: 12, margin: '4px 0 0' }}>
                  {uploadProgress.total > 0
                    ? `Processed ${uploadProgress.processed} of ${uploadProgress.total} facilities…`
                    : 'Starting upload…'}
                </p>
              </div>
            ) : null}
            <p style={{ maxWidth: 420, textAlign: 'center', color: '#64748b', fontSize: 12 }}>
            CSV (quoted fields), TSV, or Excel (.xlsx). Prefer TSV or quoted CSV for addresses with commas.
            Max 500 rows. Password is generated by the server.
            {' '}
            Allowed types: Hospital, Dental Clinic, Eye Clinic, Pharmacy, Diagnostic Centre,
            Dermatology Clinic, Laboratory, Gym &amp; Spa centre — common labels
            (Eye Care, Fitness, Specialist Hospital, Chemist, Lab, …) are auto-mapped on upload.
            </p>
          </>
        )}
        downloadHandler={() => writeArrayToCSV(
          PROVIDERS_BATCH_CSV_HEADERS,
          'facility_bulk_template.tsv',
          '\t',
        )}
      />
    </>
  );
}
